import axios from 'axios';
import { addOperationalBreadcrumb, captureFrontendException } from '../observability/sentry';
import { transformKeys, transformKeysToSnake } from './transformers';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://dev.service.contact-tel.contactpro.com.br',
  headers: {
    'Content-Type': 'application/json',
  },
});

const REFRESH_TOKEN_STORAGE_KEY = 'refreshToken';
const AUTH_SESSION_ID_STORAGE_KEY = 'sessionId';
const SWITCHED_TENANT_ID_STORAGE_KEY = 'switchedTenantId';
const SWITCHED_TENANT_NAME_STORAGE_KEY = 'switchedTenantName';
export const AUTH_STORAGE_CHANGED_EVENT = 'contacttel:auth-storage-changed';
export const REQUIRED_ACTION_PENDING_EVENT = 'contacttel:required-action-pending';

function notifyAuthStorageChanged() {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent(AUTH_STORAGE_CHANGED_EVENT));
}

function notifyRequiredActionPending(payload: unknown) {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent(REQUIRED_ACTION_PENDING_EVENT, { detail: payload }));
}

let isRefreshingToken = false;
let pendingRefreshResolvers: Array<(token: string | null) => void> = [];

function enqueuePendingRefresh(resolve: (token: string | null) => void) {
  pendingRefreshResolvers.push(resolve);
}

function flushPendingRefresh(token: string | null) {
  const resolvers = pendingRefreshResolvers;
  pendingRefreshResolvers = [];
  resolvers.forEach((resolve) => resolve(token));
}

function clearAuthStorage() {
  addOperationalBreadcrumb({
    category: 'auth.storage',
    message: 'Auth storage cleared',
    level: 'info',
    data: {
      hadToken: Boolean(localStorage.getItem('token')),
      hadRefreshToken: Boolean(localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)),
      hadSessionId: Boolean(localStorage.getItem(AUTH_SESSION_ID_STORAGE_KEY)),
    },
  });
  localStorage.removeItem('token');
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  localStorage.removeItem(AUTH_SESSION_ID_STORAGE_KEY);
  localStorage.removeItem(SWITCHED_TENANT_ID_STORAGE_KEY);
  localStorage.removeItem(SWITCHED_TENANT_NAME_STORAGE_KEY);
  notifyAuthStorageChanged();
}

function redirectToLogin() {
  if (window.location.pathname.includes('/login')) {
    return;
  }
  const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  addOperationalBreadcrumb({
    category: 'auth.redirect',
    message: 'Redirecting to login',
    level: 'warning',
    data: {
      pathname: window.location.pathname,
      returnTo,
    },
  });
  const target = `/login?returnTo=${encodeURIComponent(returnTo)}`;
  window.location.href = target;
}

function hasRefreshCredentials(): boolean {
  return Boolean(
    localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)
    && localStorage.getItem(AUTH_SESSION_ID_STORAGE_KEY),
  );
}

function isMissingRefreshCredentialsError(error: unknown): boolean {
  return error instanceof Error && error.message === 'Missing refresh credentials';
}

async function refreshAccessToken(): Promise<string> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  const sessionId = localStorage.getItem(AUTH_SESSION_ID_STORAGE_KEY);
  if (!refreshToken || !sessionId) {
    throw new Error('Missing refresh credentials');
  }

  addOperationalBreadcrumb({
    category: 'auth.refresh',
    message: 'Refreshing access token',
    level: 'info',
    data: {
      hasRefreshToken: Boolean(refreshToken),
      hasSessionId: Boolean(sessionId),
    },
  });

  const response = await axios.post(
    `${api.defaults.baseURL}/api/auth/refresh/`,
    {
      refresh: refreshToken,
      session_id: sessionId,
    },
    {
      headers: { 'Content-Type': 'application/json' },
    },
  );

  const responseData = transformKeys(response.data || {}) as {
    token?: string;
    refresh?: string;
    sessionId?: string;
  };
  const newAccessToken = String(responseData.token || '').trim();
  if (!newAccessToken) {
    throw new Error('Refresh response missing access token');
  }

  localStorage.setItem('token', newAccessToken);
  if (responseData.refresh) {
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, String(responseData.refresh));
  }
  if (responseData.sessionId) {
    localStorage.setItem(AUTH_SESSION_ID_STORAGE_KEY, String(responseData.sessionId));
  }
  notifyAuthStorageChanged();
  addOperationalBreadcrumb({
    category: 'auth.refresh',
    message: 'Access token refreshed',
    level: 'info',
    data: {
      hasRotatedRefreshToken: Boolean(responseData.refresh),
      hasRotatedSessionId: Boolean(responseData.sessionId),
    },
  });
  return newAccessToken;
}

// Interceptor para adicionar token E tenant header
api.interceptors.request.use((config) => {
  // Add auth token
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add tenant header if set (for superusers)
  const tenantId = localStorage.getItem(SWITCHED_TENANT_ID_STORAGE_KEY);
  if (tenantId) {
    config.headers['X-Tenant-Id'] = tenantId;
  }
  
  // Transform request data from camelCase to snake_case
  if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
    config.data = transformKeysToSnake(config.data);
  }
  
  return config;
});

// Interceptor para tratar respostas e erros
api.interceptors.response.use(
  (response) => {
    // Transform response data from snake_case to camelCase
    if (response.data && typeof response.data === 'object') {
      response.data = transformKeys(response.data);
    }
    return response;
  },
  async (error) => {
    const responseStatus = error.response?.status;
    const originalRequest = error.config as (typeof error.config & { _retry?: boolean });
    const requestUrl = String(originalRequest?.url || '');
    const isAuthLogin = requestUrl.includes('/api/auth/login');
    const isAuthRefresh = requestUrl.includes('/api/auth/refresh');
    const shouldAttemptRefresh = responseStatus === 401 && !isAuthLogin && !isAuthRefresh;

    if (responseStatus === 423 && error.response?.data) {
      notifyRequiredActionPending(transformKeys(error.response.data));
    }

    if (!shouldAttemptRefresh || !originalRequest || originalRequest._retry) {
      if (responseStatus === 401 && (isAuthLogin || isAuthRefresh)) {
        clearAuthStorage();
        redirectToLogin();
      }
      return Promise.reject(error);
    }

    if (!hasRefreshCredentials()) {
      addOperationalBreadcrumb({
        category: 'auth.refresh',
        message: 'Skipping access-token refresh because credentials are missing',
        level: 'info',
        data: {
          requestUrl,
          responseStatus: responseStatus ?? null,
        },
      });
      clearAuthStorage();
      redirectToLogin();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshingToken) {
      return new Promise((resolve, reject) => {
        enqueuePendingRefresh((newToken) => {
          if (!newToken) {
            reject(error);
            return;
          }
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          resolve(api(originalRequest));
        });
      });
    }

    isRefreshingToken = true;
    try {
      const newToken = await refreshAccessToken();
      flushPendingRefresh(newToken);
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      if (isMissingRefreshCredentialsError(refreshError)) {
        addOperationalBreadcrumb({
          category: 'auth.refresh',
          message: 'Refresh credentials disappeared before token refresh completed',
          level: 'info',
          data: {
            requestUrl,
            responseStatus: responseStatus ?? null,
          },
        });
      } else {
        captureFrontendException(refreshError, {
          level: 'warning',
          fingerprint: ['auth-refresh-failed'],
          tags: {
            auth_flow: 'refresh_access_token',
          },
          contexts: {
            auth_refresh: {
              request_url: requestUrl,
              response_status: responseStatus ?? null,
              had_refresh_token: Boolean(localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)),
              had_session_id: Boolean(localStorage.getItem(AUTH_SESSION_ID_STORAGE_KEY)),
            },
          },
        });
      }
      flushPendingRefresh(null);
      clearAuthStorage();
      redirectToLogin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshingToken = false;
    }
  },
);

export default api;
